import React, { FC, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { cx } from '@emotion/css';

import { DISTANCE_UNIT } from '../../../../../constants/ecp-locator';
import { useStyles } from './store-detail.styles';
import { StoreDetailProps } from './store-detail.models';
import { useStoreByType } from '../../../../../hooks/use-store-by-type';
import { roundRating, isVendingMachine } from '../../../../../utils/ecp-locator';
import { Icon, IconSizes, IconTypes } from '../../../../common';
import { scrollToTop } from '../../../../../utils/scroll/scroll-to-top';
import { StoreServices } from '../../../../common/store-services';
import { BackButton } from '../../../../common/back-button-new';
import { getRoundDistance } from '../../../../../utils/distance';
import { formatStorePhoneNumbers } from '../../../../../utils';

export const StoreDetail: FC<StoreDetailProps> = ({
  storeData,
  onStoreClose,
}): JSX.Element => {
  const classes = useStyles();
  const {
    name,
    rating,
    type,
    distance,
    street,
    openinghours,
    phonenumber,
    speciality,
    detailedLocation,
    appointmentUrl,
  } = storeData;

  const titleRef = useRef<HTMLButtonElement>(null);
  const isNormalStore = !isVendingMachine(type);
  const address = isVendingMachine(type) && detailedLocation?.length
    ? `${street}, ${detailedLocation}`
    : street;
  const formattedPhoneNumbers = formatStorePhoneNumbers(phonenumber);
  const isVendingMachineShow = isVendingMachine(type);

  useEffect(() => {
    titleRef.current?.focus();
    scrollToTop();

    return () => onStoreClose(storeData.code);
  }, []);

  const handleBackButtonClick = () => {
    onStoreClose(storeData.code);
  };

  return (
    <div
      data-testid="store-detail"
      className={classes.root}
    >
      <BackButton
        onClick={handleBackButtonClick}
      />
      <h1 className={classes.storeName}>{name}</h1>
      <div className={cx(classes.content, { [classes.contentWithBorder]: isNormalStore })}>
        <div className={classes.contentPartial}>
          <p data-testid="store-detail-rating-bar" className={classes.ratingWrapper}>
            {rating && (
              <span
                data-testid="store-detail-rating"
                className={cx(classes.pointSeparator, classes.rating)}
              >
                {roundRating(rating)}
              </span>
            )}
            <span
              data-testid="store-detail-type"
              className={classes.pointSeparator}
            >
              {useStoreByType(type)}
            </span>
            {distance && (
              <span data-testid="store-detail-distance">
                {`${getRoundDistance(distance)} ${DISTANCE_UNIT}`}
              </span>
            )}
          </p>
          {isVendingMachineShow  && (
            <p data-testid="points-only" className={classes.pointsOnly}>
              <Icon type={IconTypes.attention} aria-hidden="true" />
              <FormattedMessage id="ecp.pointsOnly.label" />
            </p>
          )}
          <p data-testid="store-detail-address" className={classes.street}>
            {street && <Icon type={IconTypes.mapMarkerBlack} size={IconSizes.xsm} aria-hidden="true" />}
            {address}
          </p>
          {isVendingMachineShow  && (
            <p data-testid="pay-card" className={classes.payCard}>
              <Icon type={IconTypes.creditCard} aria-hidden="true" />
              <FormattedMessage id="ecp.payCard.label" />
            </p>
          )}
        </div>
        <div className={classes.contentPartial}>
          <p
            data-testid="store-detail-hours"
            className={classes.hours}
          >
            {openinghours && (
              <Icon
                type={IconTypes.clockBlack}
                size={IconSizes.xs}
                aria-hidden="true"
              />
            )}
            {openinghours}
          </p>
          {formattedPhoneNumbers.length > 0 && (
            <p className={classes.phoneWrapper}>
              <Icon
                type={IconTypes.phoneBlack}
                size={IconSizes.xs}
                aria-hidden="true"
              />
              {formattedPhoneNumbers.map((formattedPhoneNumber, index) => (
                <React.Fragment key={formattedPhoneNumber}>
                  {index > 0 && ', '}
                  <a
                    data-testid="store-detail-phone"
                    href={`tel:${formattedPhoneNumber}`}
                    className={classes.phone}
                  >
                    {formattedPhoneNumber}
                  </a>
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      </div>
      {isNormalStore && <StoreServices type={type} speciality={speciality} />}
      {(isVendingMachineShow && appointmentUrl) && (
        <a
          data-testid="vending-stock-link"
          href={appointmentUrl}
          className={classes.vendingLink}
          target="_blank"
        >
          <FormattedMessage id="ecp.vending.watchStock" />
        </a>
      )}
    </div>
  );
};
